import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormArray, Validators } from '@angular/forms';
import { ITieBreakCriteriaDto } from '@models/competition/sport-parameterization/dtos/tie-break-criteria-dto';
import { IScoreTemplateWoDto } from '@models/competition/sport-parameterization/dtos/wo-scoreboard-template-dto';
import { CriteriaModalityEnum } from '@models/competition/sport-parameterization/enum/criteria-modality-type';
import { ScoreTypeEnum } from '@models/competition/sport-parameterization/enum/score-type-enum';
import { VictoryCriteriaEnum } from '@models/competition/sport-parameterization/enum/victory-criteria-enum';
import { LinkModalityCriteriaForm } from '@models/competition/sport-parameterization/forms/link-modalitycriteria-form';
import { ModalityCriteriaForm } from '@models/competition/sport-parameterization/forms/tie-break-criteria-post-form';
import { TournamentSportTemplatePostForm } from '@models/competition/sport-parameterization/forms/tournament-sport-template-post-form';
import { ScoreTemplateWoPostForm } from '@models/competition/sport-parameterization/forms/wo-scoreboard-template-post-form';
import { MetadataPutEnum } from '@models/enums/metadata-put-enum';
import { IPage } from '@models/pagination/page';
import { ISportDto } from '@models/sport-dto';
import { SportParameterizationService } from '@shared/services/competition/sport-parameterization.service';
import { LoadingService } from '@shared/services/loading.service';
import { SportService } from '@shared/services/sport.service';
import { BaseComponent } from 'src/app/core/utilities/base-component/base-component';
import { enumToDropdownOptions } from 'src/app/core/utilities/utility-functions';

@Component({
  selector: 'sigecom-sport-parameterization',
  templateUrl: './sport.parameterization-form.component.html',
})
export class SportParameterizationComponentForm
  extends BaseComponent
  implements OnChanges
{
  @Input() parameterization?: boolean;
  @Input() petBlEdicaoBloqueada: boolean = false;
  @Input() espNrIdPat?: number;
  @Input() ettNrIdSportTemplateSaved?: number;
  @Input() isEditingParametrization?: boolean;
  @Output() ettNrId = new EventEmitter<number>();

  espNrId?: number;
  isEditing: boolean = false;
  selectedSport?: ISportDto;
  defaultTemplateId?: number;

  sportsPage?: IPage<ISportDto>;
  isInitialized: boolean = true;
  isSameSaved?: number;
  defaultTemplate: boolean = false;
  desibledButtonDefaultTemplate: boolean = false;

  scoreTypeOptions = enumToDropdownOptions(ScoreTypeEnum);
  victoryCriteriaOptions = enumToDropdownOptions(VictoryCriteriaEnum);

  allTieBreakCriteriaList: ITieBreakCriteriaDto[] = [];
  tieBreakCriteriaList: ITieBreakCriteriaDto[] = [];
  selectedTieBreakCriteria: ITieBreakCriteriaDto[] = [];
  removedTieBreakCriteria: ITieBreakCriteriaDto[] = [];
  tieBreakCriteriaOptions: string[] = [];

  petBlEmbDireto: boolean = false;

  placaresWo: {
    vencedor: number;
    perdedor: number;
    ptwIdVencedor?: number;
    ptwIdPerdedor?: number;
  }[] = [{ vencedor: 0, perdedor: 0 }];

  idsRemovidosWo: number[] = [];

  criteriaModalityOptions = [
    { label: '2 equipes', value: CriteriaModalityEnum.DUAS_EQUIPES },
    { label: '3+ equipes', value: CriteriaModalityEnum.MAIS_DE_TRES_EQUIPES },
  ];

  tipoDesempateSelecionado: string = CriteriaModalityEnum.DUAS_EQUIPES; // Valor padrão

  sportForm = this.fb.group({
    entNrId: this.fb.control<number | null>(null),
    ettTxNomePeriodo: this.fb.control<string>('', Validators.required),
    ettTxTipoPlacar: this.fb.control<string | null>(null, Validators.required),
    ettNrQtdPeriodo: this.fb.control<number | null>(null, [
      Validators.required,
      Validators.min(1),
    ]),
    ettNrPontosVitoria: this.fb.control<number | null>(null, [
      Validators.required,
      Validators.min(1),
    ]),
    ettNrPontosDerrota: this.fb.control<number | null>(
      null,
      Validators.required,
    ),
    ettNrPontosVitoriaWo: this.fb.control<number | null>(null, [
      Validators.required,
      Validators.min(1),
    ]),
    ettNrPontosDerrotaWo: this.fb.control<number | null>(
      null,
      Validators.required,
    ),
    espNrId: this.fb.control<number | null>(null),
    fedNrId: this.fb.control<number | null>(null),
    ettBlPadrao: this.fb.control<boolean>(false),
    ettNrPontosEmpate: this.fb.control<number | null>(null, [
      Validators.required,
      Validators.min(0),
    ]),
    ettTxCriterioVitoriaPlacar: this.fb.control<string | null>(
      null,
      Validators.required,
    ),
  });

  woForm = this.fb.group({
    sets: this.fb.array([]),
  });

  #sportService = inject(SportService);
  #loadingService = inject(LoadingService);
  #templateService = inject(SportParameterizationService);

  ngOnInit(): void {
    this.isInitialized = true;
    const fedNrId = this.authService.getCurrentValueOfTokenData()?.fedNrId;

    this.loadAllTieBreakCriteria();

    if (this.parameterization && !this.isEditingParametrization) {
      return;
    }
    this.activatedRoute.paramMap.subscribe((params) => {
      const espNrId = params.get('espNrId');
      if (espNrId) {
        this.espNrId = +espNrId;
        this.isEditing = true;
        if (fedNrId) {
          this.loadDefaultTemplate(this.espNrId, fedNrId);
        }
      }
    });
    this.loadSport();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const fedNrId = this.authService.getCurrentValueOfTokenData()?.fedNrId;
    if (!fedNrId || !this.parameterization) return;

    if (
      changes['ettNrIdSportTemplateSaved'] &&
      changes['ettNrIdSportTemplateSaved'].currentValue
    ) {
      const currentId = changes['ettNrIdSportTemplateSaved'].currentValue;

      if (currentId !== this.sportForm.get('entNrId')?.value) {
        this.loadTemplateById(currentId);
      }
      return;
    }

    if (changes['espNrIdPat'] && changes['espNrIdPat'].currentValue) {
      if (!this.ettNrIdSportTemplateSaved) {
        this.loadDefaultTemplate(changes['espNrIdPat'].currentValue, fedNrId);
      }
    }
  }

  onToggleDefaultTemplate(isDefault: boolean): void {
    if (this.desibledButtonDefaultTemplate || !this.petBlEdicaoBloqueada) {
      return;
    }

    this.sportForm.get('ettBlPadrao')?.setValue(isDefault);
  }

  private loadTieBreakCriteria(ettNrId: number): void {
    this.#templateService.listCriteriaByTemplateId(ettNrId).subscribe({
      next: (res) => {
        const selectedCriteria = (res.content ?? []).map((item) => ({
          ...item,
          label: item.crdTxNome,
          id: item.crdNrId,
          crdNrId: item.crdNrId,
          mocNrId: item.mocNrId,
          crdTxNome: item.crdTxNome,
          mocTxTipo: item.mocTxTipo,
        }));

        this.selectedTieBreakCriteria = selectedCriteria;

        this.tipoDesempateSelecionado =
          selectedCriteria[0]?.mocTxTipo ?? CriteriaModalityEnum.DUAS_EQUIPES;

        this.updateAvailableTieBreakCriteria();
      },
    });
  }

  private resetFormsAndState() {
    this.placaresWo = [];
    this.sportForm.reset();
    this.woForm.reset();
    (this.woForm.get('sets') as FormArray).clear();
    this.selectedTieBreakCriteria = [];
    this.removedTieBreakCriteria = [];
    this.tieBreakCriteriaList = [];
    this.tipoDesempateSelecionado = CriteriaModalityEnum.DUAS_EQUIPES;
  }

  private updateAvailableTieBreakCriteria(): void {
    this.tieBreakCriteriaList = this.allTieBreakCriteriaList.filter(
      (item) =>
        !this.selectedTieBreakCriteria.some(
          (selected) => selected.crdNrId === item.crdNrId,
        ),
    );
  }

  private loadDefaultTemplate(espNrId: number, fedNrId: number): void {
    this.#templateService.getDefaultSportTemplate(fedNrId, espNrId).subscribe({
      next: (template) => {
        this.defaultTemplateId = template.ettNrId;
        this.sportForm.patchValue(template);
        this.sportForm.get('espNrId')?.setValue(template.espNrId!);
        this.sportForm.get('fedNrId')?.setValue(template.fedNrId!);
        this.sportForm.get('ettBlPadrao')?.setValue(template.ettBlPadrao!);
        this.defaultTemplate = this.sportForm.get('ettBlPadrao')?.getRawValue();
        this.sportForm
          .get('ettTxTipoPlacar')
          ?.setValue(template.ettTxTipoPlacar!);
        this.sportForm.get('entNrId')?.setValue(template.ettNrId!);
        this.sportForm
          .get('ettTxCriterioVitoriaPlacar')
          ?.setValue(template.ettTxCriterioVitoriaPlacar!);
        this.desibledButtonDefaultTemplate = false;
        this.ettNrId.emit(template.ettNrId);
        this.#templateService
          .getWoScoreboardsByTemplateId(template.ettNrId!)
          .subscribe({
            next: (woData) => {
              if (woData.length > 0) {
                const sets = this.groupWoPairs(woData);
                this.placaresWo = sets;
                this.initializeWoFormFromSets();
              }
              this.loadTieBreakCriteria(template.ettNrId!);
            },
          });
      },
      error: (error) => {
        this.defaultTemplateId = undefined;
        this.sportForm.reset();
        this.placaresWo = [];
        this.selectedTieBreakCriteria = [];
        this.messageErrorResolver.resolveMessage(error);
        this.desibledButtonDefaultTemplate = true;
      },
    });
  }

  private loadTemplateById(ettNrId: number): void {
    this.#templateService.getSportTemplateById(ettNrId).subscribe({
      next: (template) => {
        this.sportForm.patchValue(template);
        this.sportForm.get('espNrId')?.setValue(template.espNrId!);
        this.sportForm.get('fedNrId')?.setValue(template.fedNrId!);
        this.sportForm.get('ettBlPadrao')?.setValue(template.ettBlPadrao!);
        this.defaultTemplate = this.sportForm.get('ettBlPadrao')?.getRawValue();
        this.sportForm
          .get('ettTxTipoPlacar')
          ?.setValue(template.ettTxTipoPlacar!);
        this.sportForm.get('entNrId')?.setValue(template.ettNrId!);
        this.sportForm
          .get('ettTxCriterioVitoriaPlacar')
          ?.setValue(template.ettTxCriterioVitoriaPlacar!);
        this.desibledButtonDefaultTemplate = false;
        this.#templateService
          .getWoScoreboardsByTemplateId(template.ettNrId!)
          .subscribe({
            next: (woData) => {
              if (woData.length > 0) {
                const sets = this.groupWoPairs(woData);
                this.placaresWo = sets;
                this.initializeWoFormFromSets();
              }

              this.loadTieBreakCriteria(template.ettNrId!);
            },
          });
      },
      error: (error) => {
        this.messageErrorResolver.resolveMessage(error);
        this.sportForm.reset();
        this.placaresWo = [];
        this.selectedTieBreakCriteria = [];
        this.desibledButtonDefaultTemplate = true;
      },
    });
  }

  onSubmit(): void {
    // Validação: não permitir salvar sem critérios de desempate selecionados
    if (
      !this.selectedTieBreakCriteria ||
      this.selectedTieBreakCriteria.length === 0
    ) {
      return;
    }

    this.initializeWoFormFromSets();

    if (!this.parameterization) {
      this.sportForm.get('ettBlPadrao')?.setValue(true);
    }

    const formValue =
      this.sportForm.getRawValue() as TournamentSportTemplatePostForm;

    if (this.espNrIdPat) this.espNrId = this.espNrIdPat;
    formValue.espNrId = this.espNrId;

    if (this.parameterization) {
      if (formValue.ettBlPadrao) {
        // Switch ligado: sobrescreve/cria o template padrão compartilhado do esporte
        formValue.entNrId =
          this.defaultTemplateId ?? formValue.entNrId ?? undefined;
        formValue.entTxMetadado = formValue.entNrId
          ? MetadataPutEnum.ALTERADO
          : MetadataPutEnum.CRIADO;
      } else {
        // Switch desligado: cria uma cópia própria da fase, sem tocar no padrão
        formValue.entTxMetadado = MetadataPutEnum.CRIADO;
        formValue.entNrId = undefined;
      }
    } else {
      const isEditing = !!formValue.entNrId;
      formValue.entTxMetadado = isEditing
        ? MetadataPutEnum.ALTERADO
        : MetadataPutEnum.CRIADO;
    }

    // Define se W.O. e critérios de desempate devem reaproveitar os IDs
    // existentes (edição do padrão) ou serem recriados (cópia própria da fase)
    const reuseExistentIds = this.parameterization
      ? !!formValue.ettBlPadrao
      : true;

    const tokenData = this.authService.getCurrentValueOfTokenData();
    formValue.fedNrId = tokenData?.fedNrId!;
    this.#templateService.editOrCreateSportTemplate(formValue).subscribe({
      next: (savedTemplate) => {
        const ettNrId = savedTemplate.ettNrId ?? formValue.entNrId;

        if (!ettNrId) return;

        const woForms: ScoreTemplateWoPostForm[] = [];
        this.placaresWo.forEach((set, index) => {
          const periodo = index + 1;
          const periodoText = `Set ${periodo}`;

          woForms.push(
            ScoreTemplateWoPostForm.of({
              ...(set.ptwIdVencedor && reuseExistentIds
                ? {
                    entNrId: set.ptwIdVencedor,
                    entTxMetadado: MetadataPutEnum.ALTERADO,
                  }
                : { entTxMetadado: MetadataPutEnum.CRIADO }),
              ettNrId,
              ptwNrPeriodo: periodo,
              ptwTxPeriodo: periodoText,
              ptwNrPlacar: set.vencedor,
              ptwBlVencedor: true,
            }),
            ScoreTemplateWoPostForm.of({
              ...(set.ptwIdPerdedor && reuseExistentIds
                ? {
                    entNrId: set.ptwIdPerdedor,
                    entTxMetadado: MetadataPutEnum.ALTERADO,
                  }
                : { entTxMetadado: MetadataPutEnum.CRIADO }),
              ettNrId,
              ptwNrPeriodo: periodo,
              ptwTxPeriodo: periodoText,
              ptwNrPlacar: set.perdedor,
              ptwBlVencedor: false,
            }),
          );
        });

        const mappedCriteria = this.selectedTieBreakCriteria.map(
          (item, index) => {
            return ModalityCriteriaForm.of({
              crdNrId: item.crdNrId!,
              mocNrOrdem: index + 1,
              mocTxTipo: this.getValidMocTxTipo() as CriteriaModalityEnum,
              entTxMetadado:
                item.mocNrId && reuseExistentIds
                  ? MetadataPutEnum.ALTERADO
                  : MetadataPutEnum.CRIADO,
              entNrId: item.mocNrId ?? undefined,
            });
          },
        );

        const deletedCriteria = this.removedTieBreakCriteria.map((item) =>
          ModalityCriteriaForm.of({
            entNrId: item.mocNrId!,
            entTxMetadado: MetadataPutEnum.DELETADO,
          }),
        );

        const criteriaForm = LinkModalityCriteriaForm.of([
          ...mappedCriteria,
          ...deletedCriteria,
        ]);

        this.#templateService
          .linkCriteriaToModality(ettNrId, criteriaForm)
          .subscribe({
            next: () => {
              this.removedTieBreakCriteria = [];
            },
            error: (err) => this.messageErrorResolver.resolveMessage(err),
          });

        this.#templateService.saveOrUpdateWoScoreboards(woForms).subscribe({
          next: () => {
            this.#loadingService.deactivate();
            this.snackbarService.showSuccess(
              'Template do esporte atualizado com sucesso!',
            );

            if (this.parameterization) {
              this.sportForm.get('entNrId')?.setValue(ettNrId);
              if (formValue.ettBlPadrao) {
                this.defaultTemplateId = ettNrId;
              }

              this.ettNrId.emit(ettNrId);
              return;
            }

            setTimeout(
              () => this.router.navigate(['parametrizacao-esporte']),
              1500,
            );
          },
          error: (err) => {
            this.#loadingService.deactivate();
            this.messageErrorResolver.resolveMessage(err);
          },
        });
      },
      error: (err) => {
        this.#loadingService.deactivate();
        this.messageErrorResolver.resolveMessage(err);
      },
    });
  }

  addTieBreakCriteria(item: ITieBreakCriteriaDto): void {
    const mocTxTipo = this.getValidMocTxTipo() as CriteriaModalityEnum;

    this.selectedTieBreakCriteria.push({
      ...item,
      mocNrId: item.mocNrId,
      mocTxTipo: mocTxTipo,
    });
    this.updateAvailableTieBreakCriteria();
  }

  private getValidMocTxTipo(): string {
    return this.tipoDesempateSelecionado || CriteriaModalityEnum.DUAS_EQUIPES;
  }

  onWoSetsRemoved(ids: number[]): void {
    this.idsRemovidosWo = [...this.idsRemovidosWo, ...ids];
  }

  removeTieBreakCriteria(item: ITieBreakCriteriaDto): void {
    this.selectedTieBreakCriteria = this.selectedTieBreakCriteria.filter(
      (selected) => selected.crdNrId !== item.crdNrId,
    );

    if (item.mocNrId && (this.isEditing || this.parameterization)) {
      this.removedTieBreakCriteria.push(item);
    }

    this.updateAvailableTieBreakCriteria();
  }

  filterCriteria(searchText: string): void {
    if (!searchText || searchText.trim() === '') {
      this.updateAvailableTieBreakCriteria();
      return;
    }

    this.tieBreakCriteriaList = this.allTieBreakCriteriaList.filter(
      (item) =>
        !this.selectedTieBreakCriteria.some(
          (selected) => selected.crdNrId === item.crdNrId,
        ) && item.crdTxNome?.toLowerCase().includes(searchText.toLowerCase()),
    );
  }

  private loadSport(): void {
    if (!this.espNrId) {
      return;
    }
    this.#sportService.getSportById(this.espNrId).subscribe({
      next: (res) => {
        const sportFound = res;
        if (sportFound) {
          this.selectedSport = sportFound;
          this.sportForm.patchValue({
            espNrId: sportFound.espNrId,
          });
        }
      },
    });
  }
  private groupWoPairs(scores: IScoreTemplateWoDto[]): {
    vencedor: number;
    perdedor: number;
    ptwIdVencedor?: number;
    ptwIdPerdedor?: number;
  }[] {
    const grouped: {
      [key: number]: {
        vencedor?: number;
        perdedor?: number;
        ptwIdVencedor?: number;
        ptwIdPerdedor?: number;
      };
    } = {};

    for (const item of scores) {
      const periodo = item.ptwNrPeriodo!;
      if (!grouped[periodo]) grouped[periodo] = {};

      if (item.ptwBlVencedor) {
        grouped[periodo].vencedor = item.ptwNrPlacar!;
        grouped[periodo].ptwIdVencedor = item.ptwNrId!;
      } else {
        grouped[periodo].perdedor = item.ptwNrPlacar!;
        grouped[periodo].ptwIdPerdedor = item.ptwNrId!;
      }
    }

    return Object.values(grouped).map((set) => ({
      vencedor: set.vencedor ?? 0,
      perdedor: set.perdedor ?? 0,
      ptwIdVencedor: set.ptwIdVencedor,
      ptwIdPerdedor: set.ptwIdPerdedor,
    }));
  }

  private initializeWoFormFromSets(): void {
    const setsArray = this.woForm.get('sets') as FormArray;
    setsArray.clear();
    this.placaresWo.forEach((set, index) => {
      setsArray.push(
        this.fb.group({
          ptwNrPeriodo: this.fb.control<number>(index + 1, Validators.required),
          ptwTxPeriodo: this.fb.control<string>(`Set ${index + 1}`),
          ptwNrPlacar: this.fb.control<number>(set.vencedor, [
            Validators.required,
            Validators.min(0),
          ]),
          ptwBlVencedor: this.fb.control<boolean>(true),
        }),
      );
      setsArray.push(
        this.fb.group({
          ptwNrPeriodo: this.fb.control<number>(index + 1, Validators.required),
          ptwTxPeriodo: this.fb.control<string>(`Set ${index + 1}`),
          ptwNrPlacar: this.fb.control<number>(set.perdedor, [
            Validators.required,
            Validators.min(0),
          ]),
          ptwBlVencedor: this.fb.control<boolean>(false),
        }),
      );
    });
  }

  private loadAllTieBreakCriteria(): void {
    this.#templateService.listAllTieBreakCriteria().subscribe({
      next: (res) => {
        this.allTieBreakCriteriaList = (res.content ?? []).map((item) => ({
          ...item,
          label: item.crdTxNome,
          id: item.crdNrId,
          crdNrId: item.crdNrId,
          mocNrId: item.mocNrId,
        }));
        this.updateAvailableTieBreakCriteria();
      },
    });
  }

  formatEnumLabel(enumValue: string): string {
    const map = {
      PONTUACAO_TOTAL: 'PONTUAÇÃO TOTAL',
      PERIODOS_GANHOS: 'SETS VENCIDOS',
    } as Record<string, string>;
    return map[enumValue] || enumValue;
  }

  filterByName(string: string) {}

  navigateToSportParameterizationList(): void {
    this.router.navigate(['parametrizacao-esporte']).then();
  }
  moveCriteriaUp(item: ITieBreakCriteriaDto): void {
    const index = this.selectedTieBreakCriteria.indexOf(item);
    if (index > 0) {
      [
        this.selectedTieBreakCriteria[index - 1],
        this.selectedTieBreakCriteria[index],
      ] = [
        this.selectedTieBreakCriteria[index],
        this.selectedTieBreakCriteria[index - 1],
      ];
    }
  }

  moveCriteriaDown(item: ITieBreakCriteriaDto): void {
    const index = this.selectedTieBreakCriteria.indexOf(item);
    if (index < this.selectedTieBreakCriteria.length - 1) {
      [
        this.selectedTieBreakCriteria[index + 1],
        this.selectedTieBreakCriteria[index],
      ] = [
        this.selectedTieBreakCriteria[index],
        this.selectedTieBreakCriteria[index + 1],
      ];
    }
  }
}
